import { ChangeDetectorRef, Directive, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';

import { IBetDetail } from '@frontend/oxygen/bet-history-utils';
import { IBetslipBetData } from '@frontend/oxygen/betslip-utils';
import { IBYB, IBetInfo, IBetPrice } from '@frontend/oxygen/shared-utils';
import { from as observableFrom, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import * as _ from 'underscore';

import { BETSLIP_VALUES, GA_TRACKING_KEYS } from '../../../betslip/constants/bet-slip.constant';
import { BetslipBetDataUtils } from '../../../betslip/models/betslip-bet-data.utils';
import { BetslipStakeService } from '../../../betslip/services/betslip/betslip-stake.service';
import { BetslipStorageService } from '../../../betslip/services/betslip/betslip-storage.service';
import { BetslipService } from '../../../betslip/services/betslip/betslip.service';
import { OverAskService } from '../../../betslip/services/overAsk/over-ask.service';
import { ToteBetslipService } from '../../../betslip/services/toteBetslip/tote-betslip.service';
import { EnvironmentConfig } from '../../../client-config/index';
import { CmsService } from '../../../core/services/cms/cms.service';
import { IFreebetsPopupDetails, ISystemConfig } from '../../../core/services/cms/models/system-config';
import { CommandService } from '../../../core/services/communication/command/command.service';
import { PubSubService } from '../../../core/services/communication/pubsub/pubsub.service';
import { FiltersService } from '../../../core/services/filters/filters.service';
import { InfoDialogService } from '../../../core/services/infoDialogService/info-dialog.service';
import { LocaleService } from '../../../core/services/locale/locale.service';
import { TimeService } from '../../../core/services/time/time.service';
import { UserService } from '../../../core/services/user/user.service';
import { IQuaccabyb } from '../../../lazy-modules/quaccabar/components/quaccabar.model';
import { ServiceClosureService } from '../../../lazy-modules/serviceClosure/service-closure.service';
import { ILazyComponentOutput } from '../../components/lazy-component/lazy-component.model';

@Directive({ standalone: true })
export class BetslipBBDirective implements OnInit {
    @Input() isMobile: boolean;
    @Input() isBoostActive: any; // True, if odds boost activated via "Boost" button
    @Input() bybBetSlipMultiples: IBetslipBetData[];
    @Input() isFromQuaccaVal: boolean;

    isCoral: boolean;
    currencySymbol: string;
    placeStakeErr: string;
    freebetsConfig: IFreebetsPopupDetails;
    isBoostEnabled: boolean; // True, if odds boost is enabled in CMS.
    private readonly betslipType = 'betslip_stakes';
    quickStakeItems: string[] | number[];
    readonly stakePattern: string = '^(\\d{0,12}((\\.|,)\\d{0,2})?)?$';

    private readonly environment = inject(EnvironmentConfig).configuration;
    protected serviceClosureService = inject(ServiceClosureService);
    protected changeDetectorRef = inject(ChangeDetectorRef);
    protected cmsService = inject(CmsService);
    protected infoDialogService = inject(InfoDialogService);
    protected localeService = inject(LocaleService);
    protected pubSubService = inject(PubSubService);
    protected betSlipService = inject(BetslipService);
    public betslipStakeService = inject(BetslipStakeService);
    protected filterService = inject(FiltersService);
    public userService = inject(UserService);
    protected overAskService = inject(OverAskService);
    protected toteBetslipService = inject(ToteBetslipService);
    protected commandService = inject(CommandService);
    protected betslipStorageService = inject(BetslipStorageService);
    protected timeService = inject(TimeService);
    get overask(): OverAskService {
        return this.overAskService;
    }
    set overask(value: OverAskService) {}

    @Output() readonly clear = new EventEmitter();
    @Output() readonly removeByb = new EventEmitter();
    @Output() readonly setFocusInd = new EventEmitter();
    @Output() readonly setBybAmount = new EventEmitter();
    @Output() readonly handleFreebetOP = new EventEmitter();
    @Output() readonly canBoostSelectionOP = new EventEmitter();

    constructor() {
        this.serviceClosureService.checkUserServiceClosureStatus();
        this.cmsService.getQuickStakes(this.betslipType).subscribe((predefinedStakes: string[]) => {
            this.quickStakeItems = predefinedStakes;
            this.formatBetslipStakes(this.quickStakeItems);
        });

        this.cmsService.getSystemConfig().subscribe((config: ISystemConfig) => {
            this.freebetsConfig = config.FreeBets;
            this.toteBetslipService.setFreeBetsConfig(this.freebetsConfig);
        });

        this.cmsService
            .getOddsBoost()
            .pipe(
                map((config) => config.enabled),
                mergeMap((enabled) => {
                    if (!enabled) {
                        return of([false, false]);
                    } else {
                        return observableFrom(this.commandService.executeAsync(this.commandService.API.GET_ODDS_BOOST_ACTIVE)).pipe(
                            map((active) => [enabled, active]),
                        );
                    }
                }),
            )
            .subscribe((data: [boolean, boolean]) => {
                [this.isBoostEnabled] = data;
                if (this.isBoostEnabled) {
                    this.pubSubService.subscribe('BetslipBBDirective', this.pubSubService.API.ODDS_BOOST_UNSET_FREEBETS, () => {
                        this.unsetFreeBets(this.bybBetSlipMultiples);
                    });
                }
            });
    }

    ngOnInit() {
        this.currencySymbol = this.userService.currencySymbol;
        this.isCoral = this.environment && this.environment.BRAND === 'bma';
    }

    removeSelection(outcomeId: string | number): void {
        this.betSlipService.gaTracking(
            GA_TRACKING_KEYS.EVENT_TRACKING,
            GA_TRACKING_KEYS.REMOVE_ONE,
            GA_TRACKING_KEYS.NA,
            GA_TRACKING_KEYS.CLICK,
            GA_TRACKING_KEYS.NA,
        );
        this.removeByb.emit([outcomeId]);
    }

    getAggregatedPrice(bybBetslipData: IQuaccabyb) {
        let bbPrice = bybBetslipData.enhancedPrices && this.isBoostActive ? bybBetslipData.enhancedPrices : bybBetslipData.aggregatedPrice;
        if (bbPrice) {
            return bbPrice.priceTypeRef?.id === 'SP'
                ? 'SP'
                : this.userService.oddsFormat === 'frac'
                  ? bbPrice.priceNum + '/' + bbPrice.priceDen
                  : bbPrice.decimal
                    ? Number(bbPrice.decimal).toFixed(2)
                    : Number(bbPrice.price).toFixed(2);
        }
        return 'N/A';
    }

    setFocusIndex() {
        this.setFocusInd.emit();
    }

    /**
     * Set freebet to localStorage
     * @param  {IBetDetail|any} bet
     * @returns void
     */
    private setFreebet(bet: IBetDetail | IBetslipBetData | any): void {
        this.placeStakeErr = '';
        bet.errorMsg = '';
        bet.handicapErrorMsg = '';

        bet.Bet.freeBet = bet.selectedFreeBet;
        bet.stake.freeBetAmount = undefined;
        // Set free bet to be remembered for auto refresh
        this.betslipStorageService.setFreeBet(bet);
    }

    /**
     * Implementation for unset of freebets
     * @param  {} bets
     * @returns void
     */
    unsetFreeBets(bets: IBetDetail[] | any[]): void {
        if (bets && bets.length) {
            bets.forEach((bet) => {
                bet.selectedFreeBet = null;
                this.setFreebet(bet);
            });
        }
    }

    /**
     * Event Emitter to boost selection
     * @param  {IBetInfo} betslipStake
     */
    canBoostSelection(betslipStake: IBetInfo): void {
        this.canBoostSelectionOP.emit(betslipStake);
    }

    /**
     * Event emitter for freebet output
     * @param  {ILazyComponentOutput} event
     * @param  {IBetslipBetData} bet
     * @returns void
     */
    handleFreebetOutput(event: ILazyComponentOutput, bet: IBetslipBetData): void {
        this.handleFreebetOP.emit({ event: event, bet: bet });
    }

    setAmount(bet: IBetslipBetData): void {
        this.setBybAmount.emit(bet);
    }

    /**
     *Check for displaying freebet
     * @param  {IBetslipBetData} stake
     */
    isFreebetButtonShown(stake: IBetslipBetData): boolean {
        return BetslipBetDataUtils.areFreeBetsAvailable(stake) && !(this.overask.isInProcess && !this.overask.isNoBetsOffered);
    }

    /**
     * Check if use auto scroll to specific kind of notification
     * @params {String} error
     * @return {Boolean}
     */
    autoScrollOff(error: string): boolean {
        return error === BETSLIP_VALUES.ERRORS.PRICE_CHANGED || this.betSlipService.isSuspended(error);
    }

    /**
     * Filter market names
     * @param {string} name
     * @returns {string}
     */
    findWinOrEachWay(marketName: string, betslipStake: any) {
        if (betslipStake && (betslipStake.sportId === '21' || betslipStake.sport === 'HORSE_RACING')) {
            const checkMarketName = betslipStake.isFCTC ? betslipStake.Bet.betComplexName : marketName;
            return checkMarketName === 'Win or Each Way' ? 'To Win' : checkMarketName;
        } else {
            return betslipStake.isFCTC ? betslipStake.Bet.betComplexName : marketName;
        }
    }

    calculateEstReturns(bet: IBetslipBetData, index: number): number | string | void {
        const estReturn = this.betslipStakeService.calculateEstReturns(bet, index);
        return _.isNumber(estReturn) ? this.filterService.setCurrency(estReturn, this.currencySymbol) : estReturn;
    }

    /**
     *
     * @param betslipStake - betslip data
     * @param type - type of the bet
     * @param isOld - get old/ new boosted price
     * @returns bet price
     */
    getBoostedPrice(betslipStake: IBetInfo, type: string, isOld: boolean): IBetPrice {
        return isOld
            ? this.commandService.execute(this.commandService.API.ODDS_BOOST_OLD_PRICE, [betslipStake, type])
            : this.commandService.execute(this.commandService.API.ODDS_BOOST_NEW_PRICE, [betslipStake, type]);
    }

    /**
     * To check if boosts are available
     * @param betslipStake - betslip info
     * @returns - true/ false
     */
    isStakeBoostAvailable(betslipStake: IBetInfo): boolean {
        const available =
            this.userService.status &&
            this.isBoostEnabled &&
            this.isBoostActive &&
            !betslipStake.disabled &&
            !!betslipStake.Bet.oddsBoost &&
            !betslipStake.isSP &&
            !betslipStake?.bybEventsData?.every((leg) => leg.isSuspended);
        if (betslipStake.isSPLP) {
            return available && betslipStake.price.priceType !== 'SP' && betslipStake.pricesAvailable;
        }
        return available;
    }

    formatBetslipStakes(quickStakeItems: string[]) {
        this.quickStakeItems = quickStakeItems.map((stake: string) => {
            const dec = stake.split('.');
            if (dec.length > 1) {
                dec[1] = dec[1].substring(0, 2);
                stake = dec.join('.');
            }
            return stake;
        });
    }

    trackByIndex(index: number): number {
        return index;
    }

    showConfirm(bybBetslipEvents: IBYB): void {
        this.betSlipService.gaTracking(
            GA_TRACKING_KEYS.EVENT_TRACKING,
            GA_TRACKING_KEYS.REMOVE_ALL,
            GA_TRACKING_KEYS.NA,
            GA_TRACKING_KEYS.CLICK,
            GA_TRACKING_KEYS.NA,
        );
        this.infoDialogService.openInfoDialog(
            this.localeService.getString('bs.removeBetBuilder'),
            this.localeService.getString('bs.your') + bybBetslipEvents.eventName + this.localeService.getString('bs.selectionWillBeRemoved'),
            'bs-clear-dialog',
            undefined,
            undefined,
            [
                {
                    cssClass: 'btn-style4',
                    caption: this.localeService.getString('bs.no'),
                },
                {
                    caption: this.localeService.getString('bs.yes'),
                    cssClass: 'btn-style2',
                    handler: () => {
                        const outcomes = _.pluck(bybBetslipEvents.marketSelectionInfo ?? [], 'outcomeId');
                        this.removeByb.emit(outcomes);
                        this.infoDialogService.closePopUp();
                    },
                },
            ],
        );
    }

    ngOnDestroy() {
        this.pubSubService.unsubscribe('BetslipBBDirective');
    }
    /**
     *
     * @param betslipStake
     * @returns
     */
    isRacingOrVirtual(bybBetData: IBYB): boolean {
        return bybBetData.isRacingSport || bybBetData.sport === 'Virtual Sports' || bybBetData.sportId === '39';
    }
    /**
     *
     * @param betslipStake
     * @returns
     */
    getEventTime(bybBetData: IBYB): string {
        return bybBetData.isRacingSport ? `${bybBetData.localTime} ` : `${this.timeService.formatByPattern(bybBetData.time ?? '', 'HH:mm')} `;
    }
}
